import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  fr: {
    // Navigation
    nav: {
      home: "Accueil",
      expertise: "Nos Expertises",
      whoWeAre: "Qui Sommes-Nous",
      references: "Références",
      contact: "Contact"
    },
    // Hero
    hero: {
      tagline: "THE POWER OF TRANSFORMATION",
      title: "Votre partenaire de confiance pour la",
      titleHighlight: "transformation",
      subtitle: "Cabinet de conseil premium spécialisé dans l'industrie pharmaceutique. Nous accompagnons les leaders dans leur transformation digitale et organisationnelle.",
      cta: "Prendre rendez-vous",
      ctaSecondary: "Découvrir nos expertises"
    },
    // Offerings
    offerings: {
      title: "Nos Expertises",
      subtitle: "SAVOIR-FAIRE",
      sourcing: {
        title: "Sourcing & IT Strategy",
        description: "De la stratégie de sourcing au pilotage RFP, négociation et contracting. Target Operating Model, gouvernance et optimisation des coûts.",
        items: ["Target Operating Model (TOM)", "Stratégie Sourcing & IT", "Processus de sélection RFP", "Négociation et Contracting", "Support Carve-IN / Carve-OUT", "Contract Management / VMO"]
      },
      sap: {
        title: "Pilotage Transformation SAP S/4HANA",
        description: "Accompagnement bout en bout de vos projets de transformation SAP, de la stratégie à la mise en production.",
        items: ["Global Transformation Office", "Program Management", "Delivery Management", "Alignement GBPO et IT SME", "Conduite du changement"]
      },
      change: {
        title: "Management of Change & Coaching",
        description: "Coaching Management 3.0 et accompagnement au changement pour une transformation durable.",
        items: ["Design organisationnel", "Conduite du changement", "Human Capital Management", "Coaching & Formation", "Leadership Alignment"]
      },
      transition: {
        title: "Management de Transition",
        description: "Gestion des programmes de transition et pilotage de la transformation digitale.",
        items: ["Programme de transition", "Pilotage transformation digitale", "Relation client/fournisseur", "Stabilisation opérationnelle", "Gouvernance de crise"]
      }
    },
    // About
    about: {
      title: "Qui Sommes-Nous",
      subtitle: "À PROPOS",
      description: "Fondé en 2020, Walaby Consulting est un niche player de conseil spécialisé dans l'industrie pharmaceutique. Notre approche repose sur trois piliers fondamentaux:",
      pillars: {
        partnership: {
          title: "Partnership",
          description: "Nous construisons des relations durables basées sur la confiance et l'engagement mutuel."
        },
        agility: {
          title: "Agility",
          description: "Notre structure nous permet d'adapter nos interventions avec réactivité et flexibilité."
        },
        pragmatism: {
          title: "Pragmatism",
          description: "Nous privilégions les solutions concrètes et opérationnelles, orientées résultats."
        }
      },
      stats: {
        experience: "Années d'expérience",
        projects: "Projets réalisés",
        countries: "Pays",
        satisfaction: "Satisfaction client"
      }
    },
    // References
    references: {
      title: "Références",
      subtitle: "NOS RÉALISATIONS",
      description: "Découvrez quelques-unes de nos missions réalisées pour des leaders de l'industrie pharmaceutique.",
      credential1: {
        title: "Rationalisation vers un modèle global",
        client: "Leader pharmaceutique mondial",
        scope: "2 Data centers, +300 infrastructures locales, +200 applications, +30 pays",
        objective: "Unifier et professionnaliser le modèle de support IT",
        role: "Stratégie sourcing, design RFP, évaluation, négociation, transition en 3 vagues",
        outcome: "Modèle opérationnel unifié - Amélioration qualité de service - Transparence coûts"
      }
    },
    // Contact
    contact: {
      title: "Contactez-nous",
      subtitle: "PARLONS DE VOTRE PROJET",
      description: "Prêt à transformer votre organisation ? Contactez-nous pour discuter de vos besoins.",
      form: {
        name: "Nom complet",
        email: "Email professionnel",
        company: "Entreprise",
        phone: "Téléphone",
        subject: "Sujet",
        message: "Votre message",
        submit: "Envoyer le message",
        success: "Message envoyé avec succès !",
        error: "Une erreur est survenue. Veuillez réessayer."
      },
      appointment: {
        title: "Prendre rendez-vous",
        description: "Sélectionnez une date et un créneau pour un premier échange.",
        selectDate: "Sélectionner une date",
        selectTime: "Sélectionner un créneau",
        book: "Confirmer le rendez-vous",
        success: "Rendez-vous confirmé !",
        noSlots: "Aucun créneau disponible"
      },
      info: {
        address: "22 rue Eugène Givors, 94240 L'Haÿ-les-Roses, France",
        email: "contact@walaby.fr",
        phone: "+33 9 61 13 29 89",
        mobile: "+33 6 29 65 01 63"
      }
    },
    // Footer
    footer: {
      tagline: "The Power of Transformation",
      links: "Liens rapides",
      legal: "Mentions légales",
      privacy: "Politique de confidentialité",
      terms: "Conditions générales",
      copyright: "© 2024 Walaby Consulting. Tous droits réservés."
    }
  },
  en: {
    // Navigation
    nav: {
      home: "Home",
      expertise: "Our Expertise",
      whoWeAre: "Who We Are",
      references: "References",
      contact: "Contact"
    },
    // Hero
    hero: {
      tagline: "THE POWER OF TRANSFORMATION",
      title: "Your trusted partner for",
      titleHighlight: "transformation",
      subtitle: "Premium consulting firm specialized in the pharmaceutical industry. We support leaders in their digital and organizational transformation.",
      cta: "Book an appointment",
      ctaSecondary: "Discover our expertise"
    },
    // Offerings
    offerings: {
      title: "Our Expertise",
      subtitle: "KNOW-HOW",
      sourcing: {
        title: "Sourcing & IT Strategy",
        description: "From sourcing strategy to RFP management, negotiation and contracting. Target Operating Model, governance and cost optimization.",
        items: ["Target Operating Model (TOM)", "Sourcing & IT Strategy", "RFP Selection Process", "Negotiation and Contracting", "Carve-IN / Carve-OUT Support", "Contract Management / VMO"]
      },
      sap: {
        title: "SAP S/4HANA Transformation",
        description: "End-to-end support for your SAP transformation projects, from strategy to go-live.",
        items: ["Global Transformation Office", "Program Management", "Delivery Management", "GBPO and IT SME Alignment", "Change Management"]
      },
      change: {
        title: "Management of Change & Coaching",
        description: "Management 3.0 coaching and change management for sustainable transformation.",
        items: ["Organizational Design", "Change Management", "Human Capital Management", "Coaching & Training", "Leadership Alignment"]
      },
      transition: {
        title: "Transition Management",
        description: "Transition program management and digital transformation steering.",
        items: ["Transition Program", "Digital Transformation Steering", "Customer/Supplier Relationship", "Operational Stabilization", "Crisis Governance"]
      }
    },
    // About
    about: {
      title: "Who We Are",
      subtitle: "ABOUT US",
      description: "Founded in 2020, Walaby Consulting is a niche consulting player specialized in the pharmaceutical industry. Our approach is based on three fundamental pillars:",
      pillars: {
        partnership: {
          title: "Partnership",
          description: "We build lasting relationships based on trust and mutual commitment."
        },
        agility: {
          title: "Agility",
          description: "Our structure allows us to adapt our interventions with responsiveness and flexibility."
        },
        pragmatism: {
          title: "Pragmatism",
          description: "We favor concrete and operational solutions, results-oriented."
        }
      },
      stats: {
        experience: "Years of experience",
        projects: "Completed projects",
        countries: "Countries",
        satisfaction: "Client satisfaction"
      }
    },
    // References
    references: {
      title: "References",
      subtitle: "OUR ACHIEVEMENTS",
      description: "Discover some of our missions carried out for pharmaceutical industry leaders.",
      credential1: {
        title: "Rationalization into a Global Model",
        client: "Global pharmaceutical leader",
        scope: "2 Data centers, +300 local infrastructures, +200 applications, +30 countries",
        objective: "Unify and professionalize the IT support model",
        role: "Sourcing strategy, RFP design, evaluation, negotiation, 3-wave transition",
        outcome: "Unified operating model - Improved service quality - Cost transparency"
      }
    },
    // Contact
    contact: {
      title: "Contact Us",
      subtitle: "LET'S TALK ABOUT YOUR PROJECT",
      description: "Ready to transform your organization? Contact us to discuss your needs.",
      form: {
        name: "Full name",
        email: "Professional email",
        company: "Company",
        phone: "Phone",
        subject: "Subject",
        message: "Your message",
        submit: "Send message",
        success: "Message sent successfully!",
        error: "An error occurred. Please try again."
      },
      appointment: {
        title: "Book an appointment",
        description: "Select a date and time slot for an initial discussion.",
        selectDate: "Select a date",
        selectTime: "Select a time slot",
        book: "Confirm appointment",
        success: "Appointment confirmed!",
        noSlots: "No slots available"
      },
      info: {
        address: "22 rue Eugène Givors, 94240 L'Haÿ-les-Roses, France",
        email: "contact@walaby.fr",
        phone: "+33 9 61 13 29 89",
        mobile: "+33 6 29 65 01 63"
      }
    },
    // Footer
    footer: {
      tagline: "The Power of Transformation",
      links: "Quick links",
      legal: "Legal Notice",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      copyright: "© 2024 Walaby Consulting. All rights reserved."
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');

  useEffect(() => {
    // Auto-detect language based on browser settings
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang.startsWith('en')) {
      setLanguage('en');
    } else {
      setLanguage('fr');
    }
  }, []);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
